import textCenteredSVG from '@plone/volto/icons/align-center.svg';
import textLeftSVG from '@plone/volto/icons/align-left.svg';
import textRightSVG from '@plone/volto/icons/align-right.svg';

import { defineMessages } from 'react-intl';

const messages = defineMessages({
  title: { id: 'Title', defaultMessage: 'Title' },
  description: { id: 'Description', defaultMessage: 'Description' },
  cards: { id: 'Cards', defaultMessage: 'Cards' },
  blockGlobalSettings: {
    id: 'Block global settings',
    defaultMessage: 'Block global settings',
  },
  card: { id: 'Card', defaultMessage: 'Card' },
  link: { id: 'Link', defaultMessage: 'Link' },
  addCard: { id: 'Add card', defaultMessage: 'Add card' },
  Content: { id: 'Content', defaultMessage: 'Content' },
  cardsPerRow: { id: 'Cards per row', defaultMessage: 'Cards per row' },
  cardsPerRowOnMobile: {
    id: 'Cards per row on mobile',
    defaultMessage: 'Cards per row on mobile',
  },
  icon: { id: 'Icon', defaultMessage: 'Icon' },
  typeToSearch: {
    id: 'Type to search…',
    defaultMessage: 'Type to search…',
  },
  align: { id: 'Align', defaultMessage: 'Align' },
  iconStyle: { id: 'Icon style', defaultMessage: 'Icon style' },
  left: {
    id: 'Left',
    defaultMessage: 'Left',
  },
  right: {
    id: 'Right',
    defaultMessage: 'Right',
  },
  center: {
    id: 'Center',
    defaultMessage: 'Center',
  },
  lightBlueDarkBlue: {
    id: 'Light blue / Dark blue',
    defaultMessage: 'Light blue / Dark blue',
  },
  yellowDarkBlue: {
    id: 'Yellow / Dark blue',
    defaultMessage: 'Yellow / Dark blue',
  },
  greenDarkBlue: {
    id: 'Green / Dark blue',
    defaultMessage: 'Green / Dark blue',
  },
  darkBlueWhite: {
    id: 'Dark blue / White',
    defaultMessage: 'Dark blue / White',
  },
  redWhite: { id: 'Red / White', defaultMessage: 'Red / White' },
  transparentDarkBlue: {
    id: 'Transparent / Dark blue',
    defaultMessage: 'Transparent / Dark blue',
  },
  transparentLightBlue: {
    id: 'Transparent / Light blue',
    defaultMessage: 'Transparent / Light blue',
  },
  linkLabel: {
    id: 'Link label',
    defaultMessage: 'Link label',
  },
  linkLabelHelp: {
    id: 'Leave empty to hide. The card will still be clickable.',
    defaultMessage: 'Leave empty to hide. The card will still be clickable.',
  },
  openLinkInNewTab: {
    id: 'Open in a new tab',
    defaultMessage: 'Open in a new tab',
  },
  moreInfo: { id: 'More info', defaultMessage: 'More info' },
  iconDescriptionPart1: {
    id: 'Icon description part 1',
    defaultMessage: 'You can find the available icons at',
  },
  iconDescriptionPart2: {
    id: 'Icon description part 2',
    defaultMessage:
      '. For example, you can use "arrow_back" for an Arrow Back icon. You can find the name in the right menu by clicking on the icon.',
  },
});

const colors = (intl) => [
  {
    name: 'icon-transparent-dark-blue',
    label: intl.formatMessage(messages.transparentDarkBlue),
  },
];

const cardSchema = ({ intl }) => {
  return {
    title: intl.formatMessage(messages.card),
    addMessage: intl.formatMessage(messages.addCard),
    fieldsets: [
      {
        id: 'content',
        title: intl.formatMessage(messages.Content),
        fields: [
          'icon',
          'style',
          'title',
          'description',
          'href',
          'openLinkInNewTab',
          'label',
        ],
      },
    ],
    properties: {
      href: {
        title: intl.formatMessage(messages.link),
        widget: 'object_browser',
        mode: 'link',
        allowExternals: true,
      },
      openLinkInNewTab: {
        title: intl.formatMessage(messages.openLinkInNewTab),
        type: 'boolean',
      },
      title: {
        title: intl.formatMessage(messages.title),
      },
      description: {
        title: intl.formatMessage(messages.description),
        widget: 'textarea',
      },
      icon: {
        id: 'icon',
        title: intl.formatMessage(messages.icon),
        description: (
          <>
            {intl.formatMessage(messages.iconDescriptionPart1)}{' '}
            <a
              href="https://fonts.google.com/icons"
              target="_blank"
              rel="noreferrer"
            >
              Material Symbols and Icons
            </a>
            {intl.formatMessage(messages.iconDescriptionPart2)}
          </>
        ),
      },
      style: {
        title: intl.formatMessage(messages.iconStyle),
        widget: 'color_picker',
        colors: colors(intl),
        default: 'icon-transparent-dark-blue',
      },
      label: {
        title: intl.formatMessage(messages.linkLabel),
        description: intl.formatMessage(messages.linkLabelHelp),
      },
    },
    required: [],
  };
};

export const cardsSchema = ({ intl }) => {
  return {
    fieldsets: [
      {
        id: 'default',
        title: intl.formatMessage(messages.cards),
        fields: ['cards'],
      },
      {
        id: 'settings',
        title: intl.formatMessage(messages.blockGlobalSettings),
        fields: [
          'cardsPerRow',
          'cardsPerRowOnMobile',
          'align',
          //'style',
          'label',
        ],
      },
    ],
    properties: {
      cards: {
        widget: 'object_list',
        title: intl.formatMessage(messages.cards),
        schema: cardSchema,
      },
      cardsPerRow: {
        title: intl.formatMessage(messages.cardsPerRow),
        noValueOption: false,
        default: '4',
        factory: 'Choice',
        required: true,
        choices: [
          ['1', '1'],
          ['2', '2'],
          ['3', '3'],
          ['4', '4'],
          ['5', '5'],
        ],
      },
      cardsPerRowOnMobile: {
        title: intl.formatMessage(messages.cardsPerRowOnMobile),
        noValueOption: false,
        default: '1',
        factory: 'Choice',
        required: true,
        choices: [
          ['1', '1'],
          ['2', '2'],
        ],
      },
      align: {
        title: intl.formatMessage(messages.align),
        required: false,
        default: 'center',
        type: 'string',
        widget: 'buttons',
        actions: ['left', 'center', 'right'],
        actionsInfoMap: {
          left: [textLeftSVG, intl.formatMessage(messages.left)],
          center: [textCenteredSVG, intl.formatMessage(messages.center)],
          right: [textRightSVG, intl.formatMessage(messages.right)],
        },
      },
      style: {
        title: intl.formatMessage(messages.iconStyle),
        widget: 'color_picker',
        colors: colors(intl),
        default: 'icon-transparent-dark-blue',
      },
      label: {
        title: intl.formatMessage(messages.linkLabel),
        description: intl.formatMessage(messages.linkLabelHelp),
        default: intl.formatMessage(messages.moreInfo),
      },
    },
    required: [],
  };
};
